import dayjs from 'dayjs';
import {
    formatMoney, itemStatus, installmentStatus, invoiceStatus, paymentMethod, translate
} from '../../utils/dental';

// The printable treatment plan: what is to be done, what it costs, the
// instalments and what has been paid. Handed to the patient when the plan is
// agreed, and again as a statement whenever they pay.
//
// Opens a print window with the sheet as static HTML, the way the lab sheet
// does, rather than hiding a portal in the page.
//
//   plan        a plan as the API returns it (with `totals` and `schedule`)
//   kind        'plan' (the default) or 'invoice': the same figures headed as
//               an invoice, with its number, and without work that was called
//               off - an invoice lists what is charged for
//   patient     { name, phoneNumber }
//   doctorInfo  the dentist's letterhead fields
//   lang        'en' | 'ar' | 'ku'
//   t           the translator
// Returns false when the browser blocked the pop-up.

const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const date = (value) => (value ? dayjs(value).format('YYYY-MM-DD') : '-');

export const buildDentalPlanHtml = ({ plan, patient, doctorInfo = {}, lang = 'en', t, kind = 'plan' }) => {
    const rtl = lang === 'ar' || lang === 'ku';
    const tr = (key, fallback) => translate(t, key, fallback);
    const totals = plan?.totals || {};
    const currency = plan?.currency || 'IQD';
    const cash = (value) => escapeHtml(formatMoney(value, currency));
    const invoice = kind === 'invoice';
    const heading = invoice ? tr('invoice', 'Invoice') : tr('dentalPlan', 'Treatment plan');

    const items = (plan?.items || []).filter((item) => !invoice || item.status !== 'cancelled').map((item, index) => `
        <tr class="${item.status === 'cancelled' ? 'struck' : ''}">
            <td class="num">${index + 1}</td>
            <td class="num">${escapeHtml(item.tooth || '-')}</td>
            <td>${escapeHtml(item.procedure)}</td>
            <td>${escapeHtml(itemStatus.label(t, item.status))}</td>
            <td class="money">${cash(item.price)}</td>
        </tr>`).join('');

    const schedule = (plan?.schedule || []).map((row, index) => `
        <tr class="status-${row.status}">
            <td class="num">${index + 1}</td>
            <td class="num">${date(row.dueDate)}</td>
            <td class="money">${cash(row.amount)}</td>
            <td class="money">${cash(row.paid)}</td>
            <td class="money">${cash(row.remaining)}</td>
            <td>${escapeHtml(installmentStatus.label(t, row.status))}</td>
        </tr>`).join('');

    const payments = (plan?.payments || []).map((payment, index) => `
        <tr>
            <td class="num">${index + 1}</td>
            <td class="num">${date(payment.paidAt)}</td>
            <td>${escapeHtml(paymentMethod.label(t, payment.method))}</td>
            <td class="num">${escapeHtml(payment.receiptNumber || '')}</td>
            <td class="money">${cash(payment.amount)}</td>
        </tr>`).join('');

    return `<!DOCTYPE html>
<html lang="${lang}" dir="${rtl ? 'rtl' : 'ltr'}">
<head>
<meta charset="utf-8">
<title>${escapeHtml(heading)} ${escapeHtml(invoice ? plan?.invoiceNumber || '' : '')} - ${escapeHtml(patient?.name || '')}</title>
<style>
    @page { size: A4 portrait; margin: 14mm 12mm; }
    * { box-sizing: border-box; }
    body {
        font-family: 'Segoe UI', Tahoma, Arial, sans-serif;
        color: #1f2a37;
        margin: 0;
        font-size: 10.5pt;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
    .sheet { width: 186mm; margin: 0 auto; }
    header {
        display: flex; justify-content: space-between; align-items: flex-start;
        border-bottom: 2px solid #05668D; padding-bottom: 4mm; margin-bottom: 4mm;
    }
    .clinic h1 { font-size: 16pt; margin: 0 0 1mm; color: #05668D; }
    .clinic div { font-size: 9.5pt; color: #4a5a68; }
    .doc { text-align: end; font-size: 9.5pt; color: #4a5a68; }
    .doc strong { display: block; font-size: 11pt; color: #1f2a37; }
    .title { text-align: center; font-size: 13pt; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; margin: 0 0 1mm; color: #05668D; }
    .subtitle { text-align: center; font-size: 11pt; margin: 0 0 3mm; }
    .patient {
        display: grid; grid-template-columns: repeat(3, 1fr); gap: 2mm 6mm;
        background: #f3f7fa; border-radius: 2mm; padding: 3mm 4mm; margin-bottom: 4mm; font-size: 10pt;
    }
    .patient span { color: #6b7a87; font-size: 8.5pt; display: block; }
    h2 { font-size: 11pt; margin: 4mm 0 1.5mm; padding: 1.5mm 3mm; background: #e9f1f6; border-inline-start: 3px solid #05668D; }
    table { width: 100%; border-collapse: collapse; font-size: 10pt; }
    th, td { padding: 1.4mm 3mm; text-align: start; border-bottom: 1px solid #e3e9ee; }
    th { font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.04em; color: #6b7a87; font-weight: 600; }
    /* Numbers and money read left to right in every language. */
    td.num, td.money { direction: ltr; font-variant-numeric: tabular-nums; white-space: nowrap; }
    td.money, th.money { text-align: end; }
    tr.struck td { text-decoration: line-through; color: #8c8c8c; }
    tr.status-overdue td { color: #c62828; }
    tr.status-paid td { color: #2e7d32; }
    .totals { margin: 3mm 0 0; margin-inline-start: auto; width: 80mm; font-size: 10.5pt; }
    .totals div { display: flex; justify-content: space-between; padding: 1mm 3mm; }
    .totals .grand { font-weight: 700; border-top: 1.5px solid #1f2a37; margin-top: 1mm; padding-top: 1.5mm; }
    .totals .owed { font-weight: 700; color: #c62828; }
    .totals b { direction: ltr; font-variant-numeric: tabular-nums; }
    .ltr { direction: ltr; unicode-bidi: isolate; font-variant-numeric: tabular-nums; }
    .note { font-size: 9.5pt; color: #4a5a68; margin: 3mm 0 0; white-space: pre-wrap; }
    .signs { margin-top: 14mm; display: flex; justify-content: space-between; font-size: 10pt; }
    .signs div { border-top: 1px solid #1f2a37; padding-top: 1.5mm; min-width: 55mm; text-align: center; }
    footer { margin-top: 6mm; font-size: 9pt; color: #6b7a87; text-align: end; }
</style>
</head>
<body>
<div class="sheet">
    <header>
        <div class="clinic">
            <h1>${escapeHtml(doctorInfo.clinicName || doctorInfo.name || '')}</h1>
            ${doctorInfo.clinicAddress ? `<div>${escapeHtml(doctorInfo.clinicAddress)}</div>` : ''}
            ${doctorInfo.clinicPhone ? `<div>${escapeHtml(doctorInfo.clinicPhone)}</div>` : ''}
        </div>
        <div class="doc">
            <strong>${escapeHtml(doctorInfo.name || '')}</strong>
            ${doctorInfo.specialization ? `<div>${escapeHtml(doctorInfo.specialization)}</div>` : ''}
        </div>
    </header>

    <div class="title">${escapeHtml(heading)}</div>
    <div class="subtitle">${escapeHtml(plan?.title || '')}</div>

    <div class="patient">
        <div><span>${escapeHtml(tr('patientName', 'Patient'))}</span>${escapeHtml(patient?.name || '-')}</div>
        <div><span>${escapeHtml(tr('phoneNumber', 'Phone'))}</span>${escapeHtml(patient?.phoneNumber || '-')}</div>
        <div><span>${escapeHtml(tr('date', 'Date'))}</span>${date(plan?.createdAt)}</div>
        ${plan?.invoiceNumber ? `<div><span>${escapeHtml(tr('invoiceNumber', 'Invoice no.'))}</span><b class="ltr">${escapeHtml(plan.invoiceNumber)}</b></div>` : ''}
        ${invoice && plan?.invoiceStatus ? `<div><span>${escapeHtml(tr('status', 'Status'))}</span>${escapeHtml(invoiceStatus.label(t, plan.invoiceStatus))}</div>` : ''}
    </div>

    <h2>${escapeHtml(tr('dentalProcedures', 'Procedures'))}</h2>
    <table>
        <thead><tr>
            <th>#</th>
            <th>${escapeHtml(tr('tooth', 'Tooth'))}</th>
            <th>${escapeHtml(tr('dentalProcedureName', 'Procedure'))}</th>
            <th>${escapeHtml(tr('status', 'Status'))}</th>
            <th class="money">${escapeHtml(tr('price', 'Price'))}</th>
        </tr></thead>
        <tbody>${items}</tbody>
    </table>

    <div class="totals">
        <div><span>${escapeHtml(tr('subtotal', 'Subtotal'))}</span><b>${cash(totals.subtotal)}</b></div>
        ${totals.discount > 0 ? `<div><span>${escapeHtml(tr('discount', 'Discount'))}</span><b>- ${cash(totals.discount)}</b></div>` : ''}
        <div class="grand"><span>${escapeHtml(tr('total', 'Total'))}</span><b>${cash(totals.total)}</b></div>
        <div><span>${escapeHtml(tr('paid', 'Paid'))}</span><b>${cash(totals.paid)}</b></div>
        <div class="owed"><span>${escapeHtml(tr('remaining', 'Remaining'))}</span><b>${cash(totals.remaining)}</b></div>
    </div>

    ${schedule ? `
    <h2>${escapeHtml(tr('installments', 'Instalments'))}</h2>
    <table>
        <thead><tr>
            <th>#</th>
            <th>${escapeHtml(tr('dueDate', 'Due date'))}</th>
            <th class="money">${escapeHtml(tr('amount', 'Amount'))}</th>
            <th class="money">${escapeHtml(tr('paid', 'Paid'))}</th>
            <th class="money">${escapeHtml(tr('remaining', 'Remaining'))}</th>
            <th>${escapeHtml(tr('status', 'Status'))}</th>
        </tr></thead>
        <tbody>${schedule}</tbody>
    </table>` : ''}

    ${payments ? `
    <h2>${escapeHtml(tr('payments', 'Payments'))}</h2>
    <table>
        <thead><tr>
            <th>#</th>
            <th>${escapeHtml(tr('date', 'Date'))}</th>
            <th>${escapeHtml(tr('paymentMethod', 'Method'))}</th>
            <th>${escapeHtml(tr('receiptNumber', 'Receipt'))}</th>
            <th class="money">${escapeHtml(tr('amount', 'Amount'))}</th>
        </tr></thead>
        <tbody>${payments}</tbody>
    </table>` : ''}

    ${plan?.notes ? `<p class="note">${escapeHtml(plan.notes)}</p>` : ''}

    <div class="signs">
        <div>${escapeHtml(tr('patientSignature', 'Patient signature'))}</div>
        <div>${escapeHtml(tr('doctorSignature', 'Signature'))}</div>
    </div>

    <footer>${escapeHtml(tr('printedOn', 'Printed'))} ${dayjs().format('YYYY-MM-DD HH:mm')}</footer>
</div>
</body>
</html>`;
};

// The receipt for one payment: an A5 slip saying who paid how much, for what,
// and what is still owed afterwards.
//
//   payment  one entry of plan.payments
export const buildDentalReceiptHtml = ({ plan, payment, patient, doctorInfo = {}, lang = 'en', t }) => {
    const rtl = lang === 'ar' || lang === 'ku';
    const tr = (key, fallback) => translate(t, key, fallback);
    const currency = plan?.currency || 'IQD';
    const cash = (value) => escapeHtml(formatMoney(value, currency));
    const totals = plan?.totals || {};

    // What was owed once this payment had been made: everything paid up to
    // and including it, in the order the payments were taken.
    const ordered = [...(plan?.payments || [])].sort((a, b) => new Date(a.paidAt) - new Date(b.paidAt));
    const upTo = ordered.findIndex((entry) => entry._id === payment?._id);
    const paidByThen = (upTo < 0 ? ordered : ordered.slice(0, upTo + 1))
        .reduce((sum, entry) => sum + (Number(entry.amount) || 0), 0);
    const balance = Math.max(0, (totals.total || 0) - paidByThen);

    return `<!DOCTYPE html>
<html lang="${lang}" dir="${rtl ? 'rtl' : 'ltr'}">
<head>
<meta charset="utf-8">
<title>${escapeHtml(tr('receipt', 'Receipt'))} ${escapeHtml(payment?.receiptNumber || '')} - ${escapeHtml(patient?.name || '')}</title>
<style>
    @page { size: A5 landscape; margin: 10mm; }
    * { box-sizing: border-box; }
    body {
        font-family: 'Segoe UI', Tahoma, Arial, sans-serif;
        color: #1f2a37;
        margin: 0;
        font-size: 11pt;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
    .sheet { width: 188mm; margin: 0 auto; }
    header {
        display: flex; justify-content: space-between; align-items: flex-start;
        border-bottom: 2px solid #05668D; padding-bottom: 3mm; margin-bottom: 3mm;
    }
    .clinic h1 { font-size: 15pt; margin: 0 0 1mm; color: #05668D; }
    .clinic div { font-size: 9.5pt; color: #4a5a68; }
    .doc { text-align: end; font-size: 9.5pt; color: #4a5a68; }
    .doc strong { display: block; font-size: 11pt; color: #1f2a37; }
    .title { text-align: center; font-size: 13pt; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; margin: 0 0 3mm; color: #05668D; }
    .grid {
        display: grid; grid-template-columns: repeat(3, 1fr); gap: 2mm 6mm;
        background: #f3f7fa; border-radius: 2mm; padding: 3mm 4mm; margin-bottom: 4mm; font-size: 10pt;
    }
    .grid span { color: #6b7a87; font-size: 8.5pt; display: block; }
    .ltr { direction: ltr; unicode-bidi: isolate; font-variant-numeric: tabular-nums; }
    .amount {
        display: flex; justify-content: space-between; align-items: baseline;
        border: 1.5px solid #05668D; border-radius: 2mm; padding: 4mm 5mm; margin-bottom: 3mm;
    }
    .amount span { font-size: 10pt; color: #4a5a68; }
    .amount b { font-size: 18pt; color: #05668D; }
    .lines div { display: flex; justify-content: space-between; padding: 1mm 1mm; font-size: 10.5pt; border-bottom: 1px solid #e3e9ee; }
    .lines .owed b { color: #c62828; }
    .signs { margin-top: 12mm; display: flex; justify-content: space-between; font-size: 10pt; }
    .signs div { border-top: 1px solid #1f2a37; padding-top: 1.5mm; min-width: 55mm; text-align: center; }
</style>
</head>
<body>
<div class="sheet">
    <header>
        <div class="clinic">
            <h1>${escapeHtml(doctorInfo.clinicName || doctorInfo.name || '')}</h1>
            ${doctorInfo.clinicAddress ? `<div>${escapeHtml(doctorInfo.clinicAddress)}</div>` : ''}
            ${doctorInfo.clinicPhone ? `<div>${escapeHtml(doctorInfo.clinicPhone)}</div>` : ''}
        </div>
        <div class="doc">
            <strong>${escapeHtml(doctorInfo.name || '')}</strong>
            ${doctorInfo.specialization ? `<div>${escapeHtml(doctorInfo.specialization)}</div>` : ''}
        </div>
    </header>

    <div class="title">${escapeHtml(tr('receipt', 'Receipt'))}</div>

    <div class="grid">
        <div><span>${escapeHtml(tr('patientName', 'Patient'))}</span>${escapeHtml(patient?.name || '-')}</div>
        <div><span>${escapeHtml(tr('date', 'Date'))}</span><b class="ltr">${date(payment?.paidAt)}</b></div>
        <div><span>${escapeHtml(tr('receiptNumber', 'Receipt'))}</span><b class="ltr">${escapeHtml(payment?.receiptNumber || '-')}</b></div>
        <div><span>${escapeHtml(tr('dentalPlan', 'Treatment plan'))}</span>${escapeHtml(plan?.title || '-')}</div>
        <div><span>${escapeHtml(tr('invoiceNumber', 'Invoice no.'))}</span><b class="ltr">${escapeHtml(plan?.invoiceNumber || '-')}</b></div>
        <div><span>${escapeHtml(tr('paymentMethod', 'Method'))}</span>${escapeHtml(paymentMethod.label(t, payment?.method))}</div>
    </div>

    <div class="amount">
        <span>${escapeHtml(tr('amountReceived', 'Amount received'))}</span>
        <b class="ltr">${cash(payment?.amount)}</b>
    </div>

    <div class="lines">
        <div><span>${escapeHtml(tr('total', 'Total'))}</span><b class="ltr">${cash(totals.total)}</b></div>
        <div><span>${escapeHtml(tr('paid', 'Paid'))}</span><b class="ltr">${cash(paidByThen)}</b></div>
        <div class="owed"><span>${escapeHtml(tr('remaining', 'Remaining'))}</span><b class="ltr">${cash(balance)}</b></div>
    </div>

    ${payment?.note ? `<p style="font-size:10pt;color:#4a5a68;margin:3mm 0 0;white-space:pre-wrap">${escapeHtml(payment.note)}</p>` : ''}

    <div class="signs">
        <div>${escapeHtml(tr('patientSignature', 'Patient signature'))}</div>
        <div>${escapeHtml(tr('receivedBy', 'Received by'))}${payment?.receivedBy?.name ? `: ${escapeHtml(payment.receivedBy.name)}` : ''}</div>
    </div>
</div>
</body>
</html>`;
};

const openAndPrint = (html, size) => {
    const win = window.open('', '_blank', size);
    if (!win) return false;
    win.document.open();
    win.document.write(html);
    win.document.close();
    // Give the window a moment to lay out before the print dialog measures it.
    win.focus();
    setTimeout(() => {
        try {
            win.print();
        } catch (error) {
            console.error('Print failed:', error);
        }
    }, 250);
    return true;
};

export const printDentalReceipt = (args) => openAndPrint(buildDentalReceiptHtml(args), 'width=900,height=700');

export const printDentalPlan = (args) => {
    const html = buildDentalPlanHtml(args);
    const win = window.open('', '_blank', 'width=900,height=1000');
    if (!win) return false;
    win.document.open();
    win.document.write(html);
    win.document.close();
    // Give the window a moment to lay out before the print dialog measures it.
    win.focus();
    setTimeout(() => {
        try {
            win.print();
        } catch (error) {
            console.error('Print failed:', error);
        }
    }, 250);
    return true;
};

export default printDentalPlan;
